import type { LexicalNode, NodeKey, SerializedTextNode } from "lexical";
import { $applyNodeReplacement, TextNode } from "lexical";

export type SerializedTypeaheadNode = SerializedTextNode & { trigger: string; abandoned: boolean };

export class TypeaheadNode extends TextNode {
  __trigger: string;

  __abandoned: boolean;

  static getType(): string {
    return "typeahead";
  }

  static clone(node: TypeaheadNode): TypeaheadNode {
    return new TypeaheadNode(node.__trigger, node.__text, node.__key);
  }

  constructor(trigger: string, text: string, key?: NodeKey) {
    super(text, key);
    this.__trigger = trigger;
    this.__abandoned = false;
  }

  isAbandoned(): boolean {
    return this.__abandoned;
  }

  getTrigger(): string {
    return this.__trigger;
  }

  getTextWithoutTrigger(): string {
    const text = this.getTextContent();
    return text.slice(this.__trigger.length);
  }

  setAbandoned(): void {
    const writable = this.getWritable();
    writable.__abandoned = true;
  }

  isUnmergeable(): boolean {
    return this.isAbandoned();
  }

  static importJSON(serializedNode: SerializedTypeaheadNode): TypeaheadNode {
    // eslint-disable-next-line no-use-before-define
    const node = $createTypeaheadNode(serializedNode.trigger, serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    if (serializedNode.abandoned) {
      node.setAbandoned();
    }
    return node;
  }

  exportJSON(): SerializedTypeaheadNode {
    return {
      ...super.exportJSON(),
      type: TypeaheadNode.getType(),
      trigger: this.__trigger,
      abandoned: this.__abandoned,
      version: 1,
    };
  }

  setTextContent(text: string): this {
    super.setTextContent(text);
    const writable = this.getWritable();
    writable.__abandoned = false;
    return this;
  }

  spliceText(offset: number, delCount: number, newText: string, moveSelection?: boolean): TextNode {
    const result = super.spliceText(offset, delCount, newText, moveSelection);
    const writable = this.getWritable();
    writable.__abandoned = false;
    return result;
  }

  canInsertTextAfter(): boolean {
    return !this.isAbandoned();
  }

  // eslint-disable-next-line class-methods-use-this
  canInsertTextBefore(): false {
    return false;
  }

  // eslint-disable-next-line class-methods-use-this
  isTextEntity(): true {
    return true;
  }
}

export const $createTypeaheadNode = (trigger: string, text: string): TypeaheadNode =>
  $applyNodeReplacement(new TypeaheadNode(trigger, text));

export const $isTypeaheadNode = (node?: LexicalNode | null): node is TypeaheadNode => node instanceof TypeaheadNode;
